import { logger } from '@automata/utils';
import {
  ExecutorOptionsInput,
  FailureResponse,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { createSelector } from '@reduxjs/toolkit';
import { lowerCase, mapValues, pickBy, sortBy, startCase, uniq } from 'lodash';

import { WorkcellInstrumentsById, WorkflowState } from './types';
import {
  getDriverArguments,
  getInstrument,
} from '../../components/PropertiesSidebar/utils';
import {
  BACKGROUND_DRIVER_NAMES,
  DATA_EXPORT_CONNECTORS,
} from '../../constants';
import { getInstrumentDisplayName } from '../../constants/instrumentDisplayNames';
import {
  type Driver,
  DriversByNameAndVersion,
  getDriver,
} from '../../data/drivers';
import { HARDCODED_WORKCELL_OPTIONS } from '../../hooks/useGetWorkcell/types';
import type { RootState } from '../../store';
import {
  type AllTaskProperties,
  ConditionalTaskProperties,
  type DataConnectorConfig,
  type DriverByTaskId,
  type InstrumentForLocation,
  type LabwareProperties,
  type Option,
  PropertiesType,
  type TaskProperties,
  type TaskPropertiesForm,
  type TaskType,
} from '../../types';
import {
  conditionPropertiesToForm,
  getIsSingleLabwareTask,
  isStartOrStopShakeTask,
  taskPropertiesToForm,
} from '../../utils';
import { applyActionAliasesToDriver } from '../../utils/workcellActionAliases';
import { ConditionPropertiesForm } from '../../zodSchemas/conditionProperties';
import { HARDCODED_DEFAULT_SLOT_NUMBER } from '../workflowJsonSelectors/constants';

export const _selectSlice = (state: RootState): WorkflowState =>
  state.undoableState.present.workflow;

const _selectDerived = createSelector(_selectSlice, ({ derived }) => derived);

const _selectDrivers = createSelector(_selectSlice, ({ drivers }) => drivers);

export const selectDriverActionConfigs = createSelector(
  _selectSlice,
  ({ driverActionConfigs }) => driverActionConfigs
);

const _selectAliasedDrivers = createSelector(
  _selectDrivers,
  selectDriverActionConfigs,
  (drivers, driverActionConfigs) => {
    return drivers.map((driver) =>
      applyActionAliasesToDriver(driver, driverActionConfigs)
    );
  }
);

export const selectDriversByNameAndVersion = createSelector(
  _selectAliasedDrivers,
  (drivers) => {
    return drivers.reduce<DriversByNameAndVersion>(
      (acc, driver) => ({
        ...acc,
        [driver.name]: { ...acc[driver.name], [driver.version]: driver },
      }),
      {}
    );
  }
);

export const selectVisualWorkflow = createSelector(
  _selectSlice,
  (slice) => slice.visualWorkflow
);

export const selectWorkflowMetadata = createSelector(
  _selectSlice,
  (slice) => slice.metadata
);

export const selectLabwareOffsetDefinitions = createSelector(
  _selectSlice,
  (slice) => slice.labwareOffsetDefinitions
);

export const selectPlannerOptions = createSelector(
  _selectSlice,
  (slice) => slice.options.planner
);

export const selectExecutorOptions = createSelector(
  _selectSlice,
  (slice) => slice.options.executor
);

export const selectExecutorOptionsForSave = createSelector(
  selectExecutorOptions,
  (executorOptions): ExecutorOptionsInput => {
    return {
      behaviour_on_failure: 'pause' as FailureResponse,
      include_task_description_in_logs: true,
      ...executorOptions,
    };
  }
);

export const selectOptimizationTarget = createSelector(
  selectPlannerOptions,
  (planner) => planner?.optimisation_target
);

export const selectFeasibilityStage = createSelector(
  selectPlannerOptions,
  (planner) => planner?.feasibility_stage
);

export const selectAdvancedStrategyOption = createSelector(
  selectFeasibilityStage,
  (feasibilityStage) => feasibilityStage?.advanced_strategy_options
);

export const selectAdvancedStrategy = createSelector(
  selectAdvancedStrategyOption,
  (advancedStrategyOption) => advancedStrategyOption?.strategy ?? null
);

export const selectMxNOptions = createSelector(
  selectAdvancedStrategyOption,
  (advancedStrategyOption) => {
    if (advancedStrategyOption?.strategy === 'mxn') {
      return {
        batches_for_initial_chunk:
          'batches_for_initial_chunk' in advancedStrategyOption
            ? advancedStrategyOption.batches_for_initial_chunk
            : 1,
        initial_chunk_computation_time:
          'initial_chunk_computation_time' in advancedStrategyOption
            ? advancedStrategyOption.initial_chunk_computation_time
            : 60,
      };
    }
    return null;
  }
);

export const selectChunkifyOptions = createSelector(
  selectAdvancedStrategyOption,
  (advancedStrategyOption) => {
    if (advancedStrategyOption?.strategy === 'chunkify') {
      return {
        batches_per_chunk:
          'batches_per_chunk' in advancedStrategyOption
            ? advancedStrategyOption.batches_per_chunk
            : 1,
      };
    }
    return null;
  }
);

export const selectWorkflowSplittingMethod = createSelector(
  selectFeasibilityStage,
  (feasibilityStage) => feasibilityStage?.workflow_splitting_method ?? null
);

export const selectFeasibilityStageMaxComputationTime = createSelector(
  selectFeasibilityStage,
  (feasibilityStage) => feasibilityStage?.max_computation_time ?? null
);

export const selectOptimizationStages = createSelector(
  selectPlannerOptions,
  (planner) => planner?.optimisation_stages ?? []
);

export const selectShowAdvancedPlannerOptions = createSelector(
  _selectSlice,
  (slice) => slice.showAdvancedPlannerOptions
);

export const selectPlannerOptionsForSave = createSelector(
  selectPlannerOptions,
  selectShowAdvancedPlannerOptions,
  (plannerOptions, showAdvancedOptions) => {
    if (!showAdvancedOptions || !plannerOptions) {
      return {
        optimisation_target:
          plannerOptions?.optimisation_target ?? 'minimize_total_plan_duration',
        max_computation_time: plannerOptions?.max_computation_time ?? null,
      };
    }
    return plannerOptions;
  }
);

export const selectLastPlanningTime = createSelector(
  _selectSlice,
  (slice) => slice.lastPlanningTime
);

export const selectSchedulerVersion = createSelector(
  _selectSlice,
  (slice) => slice.schedulerVersion
);

export const selectCommentsById = createSelector(
  selectVisualWorkflow,
  ({ comments }): Record<string, string> => comments
);

export const selectLabwareProperties = createSelector(
  selectVisualWorkflow,
  ({ labwareProperties }): Record<string, LabwareProperties> =>
    labwareProperties
);

export const selectTaskPropertiesStrict = createSelector(
  selectVisualWorkflow,
  ({ taskProperties }): Record<string, AllTaskProperties> => taskProperties
);

export const selectActionTaskPropertiesStrict = createSelector(
  selectVisualWorkflow,
  ({ taskProperties }): Record<string, TaskProperties> => {
    return Object.entries(taskProperties)
      .filter(
        ([_, property]) => property.propertiesType === PropertiesType.Action
      )
      .reduce((prev, [key, property]) => {
        return {
          ...prev,
          [key]: property,
        };
      }, {});
  }
);

export const selectConditionalTaskPropertiesStrict = createSelector(
  selectVisualWorkflow,
  ({ taskProperties }): Record<string, ConditionalTaskProperties> => {
    return Object.entries(taskProperties)
      .filter(
        ([_, property]) =>
          property.propertiesType === PropertiesType.Conditional
      )
      .reduce((prev, [key, property]) => ({ ...prev, [key]: property }), {});
  }
);

type ConditionPropertiesFormByTaskId = Record<string, ConditionPropertiesForm>;
export const selectConditionalTaskPropertiesForm = createSelector(
  selectConditionalTaskPropertiesStrict,
  (conditionProperties): ConditionPropertiesFormByTaskId => {
    return Object.entries(conditionProperties).reduce(
      (acc, [id, properties]) => {
        return { ...acc, [id]: conditionPropertiesToForm(properties) };
      },
      {} as ConditionPropertiesFormByTaskId
    );
  }
);

export const selectTaskPropertiesPreview = createSelector(
  selectVisualWorkflow,
  (visualWorkflow): Record<string, AllTaskProperties> => {
    const assignHardcodedSlot = (
      taskProperties: Record<string, AllTaskProperties>
    ) =>
      Object.entries(taskProperties).reduce((acc, [taskId, taskProperties]) => {
        if (taskProperties.propertiesType !== PropertiesType.Action) {
          return {
            ...acc,
            [taskId]: taskProperties,
          };
        }

        return {
          ...acc,
          [taskId]: {
            ...taskProperties,
            taskParameters: {
              slot: HARDCODED_DEFAULT_SLOT_NUMBER,
              ...taskProperties.taskParameters,
            },
          },
        };
      }, {});
    return assignHardcodedSlot(visualWorkflow.taskProperties);
  }
);

type TaskPropertiesFormByTaskId = Record<string, TaskPropertiesForm>;
export const selectTaskPropertiesForm = createSelector(
  selectTaskPropertiesStrict,
  (taskProperties): TaskPropertiesFormByTaskId => {
    return Object.entries(taskProperties).reduce<TaskPropertiesFormByTaskId>(
      (acc, [id, properties]) => {
        return properties.propertiesType !== PropertiesType.Action
          ? acc
          : { ...acc, [id]: taskPropertiesToForm(properties) };
      },
      {}
    );
  }
);

export const selectUnitsConsumedByTaskByLabware = createSelector(
  selectActionTaskPropertiesStrict,
  (
    taskProperties
  ): {
    [taskId: string]: { [labwareId: string]: number | null | undefined };
  } => {
    return mapValues(taskProperties, (p) => p.labwareIdsToUnitsConsumed);
  }
);

export const selectTaskIds = createSelector(
  selectTaskPropertiesStrict,
  (tasks) => Object.keys(tasks)
);

export const selectLabwareIds = createSelector(
  selectLabwareProperties,
  (labware) => Object.keys(labware)
);

export const selectWorkcellInstruments = createSelector(
  _selectSlice,
  ({ workcellInstruments }): WorkcellInstrumentsById => workcellInstruments
);

export const selectDriversByInstrumentId = createSelector(
  selectDriversByNameAndVersion,
  selectWorkcellInstruments,
  (drivers, workcellInstruments): Record<string, Driver> => {
    return Object.entries(workcellInstruments).reduce<Record<string, Driver>>(
      (acc, [instrumentId, { driverName, driverVersion }]) => {
        const driver = getDriver(drivers, { driverName, driverVersion });
        if (driver === undefined) {
          logger.error(
            `No driver name "${driverName}". Referenced by instrument with ID: "${instrumentId}"`
          );
          return acc;
        }
        return { ...acc, [instrumentId]: driver };
      },
      {}
    );
  }
);

export const selectDriverForTaskByTaskId = createSelector(
  selectDriversByInstrumentId,
  selectTaskPropertiesStrict,
  (drivers, taskPropertiesById): DriverByTaskId => {
    return Object.entries(taskPropertiesById).reduce<{
      [taskId: string]: Driver;
    }>((acc, [taskId, taskProperties]) => {
      if (taskProperties.propertiesType !== PropertiesType.Action) {
        return acc;
      }
      if (taskProperties.instrumentId != null) {
        const driver = drivers[taskProperties.instrumentId];
        if (driver !== undefined)
          return {
            ...acc,
            [taskId]: driver,
          };
      }
      return acc;
    }, {});
  }
);

// TODO IMMEDIATELY test, make clearer name
export const selectInstrumentDataById = createSelector(
  selectDriversByInstrumentId,
  selectWorkcellInstruments,
  (
    driversByInstrumentId,
    workcellInstruments
  ): Record<string, InstrumentForLocation> => {
    return Object.entries(workcellInstruments).reduce<
      Record<string, InstrumentForLocation>
    >((acc, [id, instrument]) => {
      const {
        bench,
        capacity,
        config,
        dataConnectorConfig,
        driverName,
        driverVersion,
        is_slotless_container,
        mappings,
        simulate,
        display_name,
      } = instrument;
      const driver = driversByInstrumentId[id];
      if (driver === undefined) {
        logger.error('no driver', { driverName, id });
        return acc;
      }

      const capabilities: string[] = Object.keys(driver.actions).filter(
        (actionName) => {
          // These instruments don't have start and stop actions.
          const instruments = ['cytomat10_1', 'cytomatC425_1'];
          if (instruments.includes(id) && isStartOrStopShakeTask(actionName)) {
            return false;
          }

          return driver.actions[actionName]?.is_sub_action !== true;
        }
      );
      capabilities.sort();

      const dataConnector = dataConnectorConfig ? { dataConnectorConfig } : {};
      const instrumentData: InstrumentForLocation = {
        ...dataConnector,
        id,
        bench,
        // TODO(IL, 2024-04-25): make metadata.model, metadata.manufacturer, (and max_capacity?) required fields
        capacity,
        config,
        name: getInstrumentDisplayName(
          id,
          display_name ?? startCase(lowerCase(id))
        ),
        image: 'imgTodo.jpg',
        manufacturer: driver.metadata?.manufacturer ?? 'NO MANUFACTURER',
        capabilities,
        driverName,
        driverVersion,
        is_slotless_container,
        mappings,
        simulate,
      };
      return { ...acc, [id]: instrumentData };
    }, {});
  }
);

/**
 * Finds driver arguments for a given task properties.
 *
 * @param {TaskProperties} taskProperties - task properties object.
 *
 * @returns {DriverArguments | null} - driver arguments or null if not found.
 **/
export const selectDriverArgumentsByTaskProperties = (
  taskProperties: TaskProperties | null
) =>
  createSelector(
    /**
     * @param {Record<string, Driver>} driversByTaskId - object with driver configurations by taskId (from realDrivers.json).
     * @param {Record<string, InstrumentForLocation>} instrumentsById - object with instrument configurations by instrumentId (from workcell definitions).
     */
    [selectDriverForTaskByTaskId, selectInstrumentDataById],
    (driversByTaskId, instrumentsById) => {
      if (taskProperties?.propertiesType !== PropertiesType.Action) return null;

      const driver = driversByTaskId[taskProperties.id];

      if (!driver || !taskProperties.taskType) {
        return null;
      }

      const driverAction = driver.actions[taskProperties.taskType];
      const instrument = getInstrument(
        taskProperties.instrumentId,
        instrumentsById
      );
      const isSingleLabware = getIsSingleLabwareTask(taskProperties.taskType);

      return getDriverArguments(
        driverAction ?? null,
        instrument ?? null,
        isSingleLabware,
        taskProperties.taskType
      );
    }
  );

/**
 * Finds instruments with their data connector configuration for exportData tasks.
 *
 * @param {Record<string, InstrumentForLocation>} instruments - object with instrument configurations by tinstrumentId (from workcell defenitions).
 *
 * @returns an object with instrument data connector configurations for dataExport tasks by instrumentId.
 */
export const selectDataExportInstrumentsConfigsById = createSelector(
  selectInstrumentDataById,
  (instruments): Record<string, DataConnectorConfig> => {
    return pickBy(
      mapValues(
        instruments,
        (instrument: InstrumentForLocation) => instrument.dataConnectorConfig
      ),
      (dataConnectorConfig) => !!dataConnectorConfig
    );
  }
);

/**
 * Finds driver configurations for exportData tasks.
 *
 * @param {Record<string, Driver>} drivers - object with driver configurations by taskId (from realDrivers.json).
 *
 * @returns an object with drivers for exportData tasks.
 */
export const selectDataExportOutputDriversById = createSelector(
  selectDriversByInstrumentId,
  selectInstrumentDataById,
  (drivers, instruments): Record<string, Driver> => {
    // gets instruments which have data connector config
    const dataExportInstruments = pickBy(
      instruments,
      (instrument) => !!instrument.dataConnectorConfig
    );
    // gets drivers only for those instruments which have data connector config (dataExportInstruments) by id
    return pickBy(drivers, (_, id) => dataExportInstruments.hasOwnProperty(id));
  }
);

export const selectDataExportOutputConnectorsById = createSelector(
  selectDataExportInstrumentsConfigsById,
  (instrumentsConfigs): Option[] => {
    // if option is not found, the default option will be shown
    // to avoid this, we need to add a new option to DATA_EXPORT_CONNECTORS
    const defaultOption: Option = { id: '', name: 'No Option' };

    // for each instrument found we must find the corresponding option
    return Object.keys(instrumentsConfigs).map(
      (key) => DATA_EXPORT_CONNECTORS[key] ?? defaultOption
    );
  }
);

// NOTE(IL, 2024-03-29): it's valid to have no starting instrument (all labware start with startLocation: null)
// so those labware with no startLocation will just be omitted from this object
export const selectLabwareStartingInstrumentDisplayNames = createSelector(
  selectLabwareProperties,
  selectInstrumentDataById,
  (labware, instruments): Record<string, string> =>
    Object.entries(labware).reduce<Record<string, string>>(
      (acc, [labwareId, { startLocation }]) => {
        if (startLocation === null) {
          // normal case, labware just doesn't have an instrument defined
          return acc;
        }
        const instrument = instruments[startLocation];
        if (instrument === undefined) {
          logger.error(
            `Labware ${labwareId} has startLocation ${startLocation}, but that instrument ID does not exist`
          );
          return acc;
        }

        return { ...acc, [labwareId]: instrument.name };
      },
      {}
    )
);

export const selectTasksFromWorkcellInstruments = createSelector(
  selectInstrumentDataById,
  (instrumentsById): TaskType[] =>
    sortBy(
      uniq(
        Object.values(instrumentsById).reduce<TaskType[]>(
          (acc, { capabilities, driverName }) => {
            // don't include actions directly from the eval/sftp drivers etc
            const isBackgroundDriver =
              BACKGROUND_DRIVER_NAMES.includes(driverName);

            if (isBackgroundDriver) return acc;

            return [...acc, ...capabilities];
          },
          []
        )
      )
    )
);

export const selectWorkcellName = createSelector(
  selectVisualWorkflow,
  ({ workcellName }): string => workcellName
);

export const selectIsWorkflowPublished = createSelector(
  _selectDerived,
  ({ published }): boolean => published
);

export const selectWorkflowAuthor = createSelector(
  selectVisualWorkflow,
  ({ workflowAuthor }): string => workflowAuthor
);

export const selectWorkflowDescription = createSelector(
  selectVisualWorkflow,
  ({ workflowDescription }): string => workflowDescription
);

export const selectWorkflowName = createSelector(
  selectVisualWorkflow,
  ({ workflowName }): string => workflowName
);

export const selectWorkflowBatchNumber = createSelector(
  selectVisualWorkflow,
  ({ batches }): number => batches
);

export const selectScripts = createSelector(
  _selectSlice,
  selectInstrumentDataById,
  (slice, instrumentDataById) => {
    // Available functions should be in the "evaluator" config.
    const availableFunctions = instrumentDataById.evaluator?.config
      ?.functions as string[] | undefined;

    if (availableFunctions) {
      // Only allowed functions should be shown.
      const filteredFunctions = slice.scripts.functions.filter((script) =>
        availableFunctions.includes(script.function_name)
      );

      return {
        ...slice.scripts,
        functions: filteredFunctions,
      };
    }

    // If there is no config, we should allow all functions. In this case, the
    // "evaluator" driver is loaded on the backend anyway, and the workflow
    // is deployed as a workflow with only one allowed function (selected one).
    return slice.scripts;
  }
);

export const selectWorkcellTransportConfigId = createSelector(
  _selectSlice,
  (slice) => slice.workcellTransportConfigId
);

export const selectWorkcellTransportConfigType = createSelector(
  _selectSlice,
  (slice) => slice.workcellTransportConfigType
);

export const selectWorkcellTransportConfigVersion = createSelector(
  _selectSlice,
  (slice) => slice.workcellTransportConfigVersion
);

export const selectSelectedWorkcellId = createSelector(
  _selectSlice,
  (slice) => {
    return slice.selectedWorkcellId;
  }
);

export const selectSelectedWorkcellName = createSelector(
  _selectSlice,
  (slice) => {
    return (
      HARDCODED_WORKCELL_OPTIONS.find(
        ({ id }) => id === slice.selectedWorkcellId
      )?.name || ''
    );
  }
);

export const selectParameterDefinitions = createSelector(
  selectVisualWorkflow,
  // added fallback to fix old workflows
  ({ parameterDefinitions }) => parameterDefinitions ?? []
);

export const selectParameterValues = createSelector(
  selectVisualWorkflow,
  ({ parameterValues }) => parameterValues ?? []
);

export const selectAppliedParameterDefinitions = createSelector(
  selectVisualWorkflow,
  ({ parameterDefinitions, taskProperties }) => {
    if (!parameterDefinitions || !taskProperties) return [];

    return parameterDefinitions.filter((parameter) =>
      // check all the applied parameters for the current task
      Object.values(taskProperties).some((task) => {
        // Only check tasks that have taskAppliedParameters (e.g., action tasks)
        if (
          'taskAppliedParameters' in task &&
          typeof task.taskAppliedParameters === 'object' &&
          task.taskAppliedParameters !== null
        ) {
          // check if the parameter from the list matches the parameter set in taskProperties
          return Object.values(task.taskAppliedParameters ?? {}).some(
            (parameterId) => parameterId === parameter.id
          );
        }
        return false;
      })
    );
  }
);

export const selectInstrumentsForDropdown = (taskType: TaskType) =>
  createSelector(selectInstrumentDataById, (instrumentsById) =>
    Object.values(instrumentsById).filter((inst) =>
      inst.capabilities.includes(taskType)
    )
  );
